/**
 * Séance de révision (design system, EcranSeance ; spécification §5), reliée à
 * ServiceSeance : chaque note est enregistrée avant que la séance avance.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clesCartesFilles, differenceCartesFilles, type CarteMere } from '../../cartes/carte-mere';
import {
  actionDeLaTouche,
  NOTE_DE_L_ACTION,
  TOUCHE_RETOURNER,
  TOUCHES_PAR_DEFAUT,
} from '../../clavier/touches';
import { State, type Note } from '../../fsrs/planificateur';
import { seuilAlerteEffectif } from '../../paquets/reglages';
import { estTerminee } from '../../seance/seance';
import { ErreurDonnees } from '../../supabase/depot';
import { ServiceSeance } from '../../supabase/service-seance';
import { Bandeau } from '../composants/Bandeau';
import { Bouton } from '../composants/Bouton';
import { Chargement } from '../composants/Chargement';
import { Dialogue } from '../composants/Dialogue';
import { EtatVide } from '../composants/EtatVide';
import { Icone } from '../composants/Icone';
import { useNotifier } from '../composants/Notifications';
import { Touche } from '../composants/Touche';
import { FournisseurImages } from '../contenu/ContenuDeCarte';
import { useDonnees } from '../donnees/contexte';
import { messageErreur } from '../donnees/erreurs';
import { useImages } from '../donnees/images';
import { useDispositionClavier } from '../donnees/touches';
import type { EditeurPoignee } from '../editeur/Editeur';
import {
  brouillonDepuisCarte,
  carteDuBrouillon,
  erreursDuBrouillon,
  FormulaireCarte,
  type Brouillon,
  type ErreursCarte,
} from '../editeur/FormulaireCarte';
import { decrireEcheance, dureeDeNotation } from '../format';
import { dansUnChamp } from '../outils';
import { naviguer } from '../routes';
import { BarreDeNotation, type DureeDeNotation } from './BarreDeNotation';
import { BarreDeSeance } from './BarreDeSeance';
import { CarteDeRevision } from './CarteDeRevision';
import { AlerteDeFinDePhase, BilanDeSeance } from './FinDeSeance';
import { MinuteurDeRetournement } from './MinuteurDeRetournement';

type Vue =
  | 'chargement'
  | 'carte'
  | 'modification'
  | 'fin-de-phase'
  | 'terminee'
  | 'quittee'
  | 'rien'
  | 'erreur';

interface Reponse {
  carteId: string;
  note: Note;
}

/** Erreur de réseau : la réponse n'a pas pu partir. */
function estHorsLigne(e: unknown): boolean {
  return (
    e instanceof TypeError || (e instanceof Error && /fetch|network|Failed to/i.test(e.message))
  );
}

export function EcranSeance({ paquetId }: { paquetId: string }) {
  const { depot, paquets, preferences, jours, rechargerJours } = useDonnees();
  const notifier = useNotifier();
  const images = useImages();
  const caractere = useDispositionClavier();
  const service = useRef<ServiceSeance | null>(null);
  const [, setVersion] = useState(0);
  const rafraichir = () => setVersion((v) => v + 1);
  const [vue, setVue] = useState<Vue>('chargement');
  const [face, setFace] = useState<'recto' | 'verso'>('recto');
  const [cartesMeres, setCartesMeres] = useState(new Map<string, CarteMere>());
  const [reponses, setReponses] = useState<Reponse[]>([]);
  const [totaux, setTotaux] = useState(new Map<number, number>());
  const [occupe, setOccupe] = useState(false);
  const [horsLigne, setHorsLigne] = useState<(() => Promise<void>) | null>(null);
  const [quitter, setQuitter] = useState(false);
  const [noteAppuyee, setNoteAppuyee] = useState<Note | null>(null);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [erreursModification, setErreursModification] = useState<ErreursCarte>({});
  const [alerte, setAlerte] = useState<{ revisees: number; apprentissage: number } | null>(null);
  const debutSeance = useRef(Date.now());
  const debutCarte = useRef(Date.now());
  const debutPause = useRef<number | null>(null);
  const tailleListe = useRef(0);
  const formulaire = useRef<EditeurPoignee>(null);

  const touches = preferences?.touches ?? TOUCHES_PAR_DEFAUT;
  const nomPaquet = paquets?.find((p) => p.id === paquetId)?.nom ?? '';
  const s = service.current;
  const courante = s?.carteCourante ?? null;
  const reglages = s?.seance.contexte.reglages ?? null;
  const carteMere = courante ? (cartesMeres.get(courante.carteMereId) ?? null) : null;

  // Notifications au-dessus de la barre de notation.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--fc-notification-bas',
      'calc(var(--hauteur-note) + 2 * var(--espace-16) + var(--espace-12))',
    );
    document.title = `Séance — ${nomPaquet || 'Flashcard'}`;
    return () => {
      document.documentElement.style.removeProperty('--fc-notification-bas');
    };
  }, [nomPaquet]);

  /** Mémorise le nombre de cartes de chaque phase, à son début. */
  const noterPhase = useCallback((service: ServiceSeance) => {
    const { phase, file } = service.seance;
    setTotaux((actuels) => {
      if (actuels.has(phase) || file.length === 0) return actuels;
      return new Map(actuels).set(phase, file.length + service.seance.vues.length);
    });
  }, []);

  const demarrer = useCallback(async () => {
    setVue('chargement');
    try {
      const nouveau = await ServiceSeance.demarrer(depot, paquetId);
      service.current = nouveau;
      tailleListe.current = nouveau.seance.file.length;
      debutSeance.current = Date.now();
      setReponses([]);
      setTotaux(new Map());
      noterPhase(nouveau);
      setFace('recto');
      setVue(estTerminee(nouveau.seance) ? 'rien' : 'carte');
    } catch (e) {
      notifier({ message: messageErreur(e), statut: 'danger' });
      setVue('erreur');
    }
  }, [depot, paquetId, noterPhase, notifier]);

  useEffect(() => {
    notifier.effacer();
    void demarrer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demarrer]);

  // Contenu de la carte en cours, et de la suivante à l'avance.
  useEffect(() => {
    if (!s) return;
    const ids = s.seance.file.slice(0, 2).map((id) => s.cartes.get(id)?.carteMereId);
    for (const id of ids) {
      if (!id || cartesMeres.has(id)) continue;
      void depot
        .carteMere(id)
        .then((lue) => setCartesMeres((m) => new Map(m).set(id, lue.carte)))
        .catch((e) => notifier({ message: messageErreur(e), statut: 'danger' }));
    }
  });

  // Chronomètre : il part quand la carte s'affiche.
  useEffect(() => {
    debutCarte.current = Date.now();
  }, [courante?.id, carteMere !== null]);

  const durees = useMemo((): Record<Note, DureeDeNotation> | null => {
    if (!s || !courante || face !== 'verso') return null;
    const apercu = s.apercu(new Date());
    if (!apercu) return null;
    const etat = courante.fsrs.state;
    const reapprentissage = etat === State.Review || etat === State.Relearning;
    const resultat = {} as Record<Note, DureeDeNotation>;
    for (const note of [1, 2, 3, 4] as const) {
      const r = apercu[note];
      resultat[note] = {
        texte: dureeDeNotation(r.dureeMs),
        source: r.parFsrs ? 'fsrs' : 'etapes',
        aide: r.parFsrs
          ? `Durée calculée par FSRS : la carte ${etat === State.Review ? 'reste' : 'devient'} acquise`
          : `Durée fixée par les étapes ${reapprentissage ? 'de réapprentissage' : 'd’apprentissage'}`,
      };
    }
    return resultat;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s, courante?.id, courante?.fsrs.reps, face]);

  /** Après une note, une suspension ou une modification : alerte, fin, ou carte suivante. */
  function apres(alerteSeuil: boolean) {
    const svc = service.current!;
    noterPhase(svc);
    setFace('recto');
    if (alerteSeuil) {
      setAlerte({
        revisees: totaux.get(1) ?? tailleListe.current,
        apprentissage: svc.seance.file.length,
      });
      setVue('fin-de-phase');
    } else if (estTerminee(svc.seance)) {
      setVue('terminee');
      void rechargerJours();
    } else setVue('carte');
    rafraichir();
  }

  async function executer(action: () => Promise<void>) {
    setOccupe(true);
    try {
      await action();
      setHorsLigne(null);
    } catch (e) {
      if (estHorsLigne(e)) {
        setHorsLigne(() => () => executer(action));
      } else if (e instanceof ErreurDonnees && e.code === '40001') {
        notifier({ message: messageErreur(e), statut: 'danger' });
        await demarrer();
      } else {
        notifier({ message: messageErreur(e), statut: 'danger' });
      }
    } finally {
      setOccupe(false);
    }
  }

  function retourner() {
    if (vue === 'carte' && face === 'recto' && carteMere) setFace('verso');
  }

  function noter(note: Note) {
    const svc = service.current;
    const carte = svc?.carteCourante;
    if (!svc || !carte || occupe || face !== 'verso') return;
    const temps = Date.now() - debutCarte.current;
    void executer(async () => {
      const resultat = await svc.noter(note, temps);
      setReponses((r) => [...r, { carteId: carte.id, note }]);
      apres(resultat.alerteSeuil);
    });
  }

  function annuler() {
    const svc = service.current;
    if (!svc || occupe || reponses.length === 0) return;
    void executer(async () => {
      const carteId = await svc.annuler();
      if (carteId === null) return;
      setReponses((r) => r.slice(0, -1));
      const phaseRestauree = svc.seance.phase;
      setTotaux((actuels) => new Map([...actuels].filter(([p]) => p <= phaseRestauree)));
      setAlerte(null);
      setFace('recto');
      setVue('carte');
      rafraichir();
      notifier({ message: 'Réponse annulée.', icone: 'undo-2' });
    });
  }

  function suspendre() {
    const svc = service.current;
    const carte = svc?.carteCourante;
    if (!svc || !carte || occupe || face !== 'verso') return;
    void executer(async () => {
      const { alerteSeuil } = await svc.suspendreCarteCourante();
      apres(alerteSeuil);
      notifier({
        message: 'Carte suspendue.',
        icone: 'circle-pause',
        action: {
          libelle: 'Annuler',
          onAction: async () => {
            await depot.suspendre(carte.id, false);
            notifier({
              message: 'Carte réactivée : elle reviendra à la prochaine séance.',
              statut: 'succes',
            });
          },
        },
      });
    });
  }

  function ouvrirModification() {
    if (!carteMere || face !== 'verso' || vue !== 'carte') return;
    debutPause.current = Date.now();
    setBrouillon(brouillonDepuisCarte(carteMere, preferences?.derniersModes));
    setErreursModification({});
    setVue('modification');
    window.setTimeout(() => formulaire.current?.focus(), 50);
  }

  function fermerModification() {
    if (debutPause.current !== null) debutCarte.current += Date.now() - debutPause.current;
    debutPause.current = null;
    setBrouillon(null);
    setVue('carte');
  }

  async function enregistrerModification() {
    const svc = service.current;
    const carte = svc?.carteCourante;
    if (!svc || !carte || !carteMere || !brouillon) return;
    const erreurs = erreursDuBrouillon(brouillon);
    setErreursModification(erreurs);
    if (Object.keys(erreurs).length > 0) return;
    const nouvelle = carteDuBrouillon(brouillon);
    setOccupe(true);
    try {
      const lue = await depot.carteMere(carte.carteMereId);
      const { carte: enregistree } = await depot.enregistrerCarteMere(
        carte.carteMereId,
        lue.paquetId,
        nouvelle,
      );
      setCartesMeres((m) => new Map(m).set(carte.carteMereId, enregistree));
      const { aSupprimer } = differenceCartesFilles(
        clesCartesFilles(carteMere),
        clesCartesFilles(enregistree),
      );
      const supprimees = [...svc.cartes.values()]
        .filter((c) => c.carteMereId === carte.carteMereId && aSupprimer.includes(c.cle))
        .map((c) => c.id);
      fermerModification();
      if (supprimees.length > 0) {
        const { alerteSeuil } = svc.retirerCartesSupprimees(supprimees);
        if (supprimees.includes(carte.id)) {
          notifier({
            message:
              'Carte enregistrée. Elle ne demande plus cette réponse : passage à la suivante.',
            statut: 'succes',
          });
          apres(alerteSeuil);
          return;
        }
      }
      notifier({ message: 'Carte enregistrée.', statut: 'succes' });
      rafraichir();
    } catch (e) {
      notifier({ message: messageErreur(e), statut: 'danger' });
    } finally {
      setOccupe(false);
    }
  }

  function demanderQuitter() {
    if (
      vue === 'terminee' ||
      vue === 'quittee' ||
      vue === 'rien' ||
      vue === 'erreur' ||
      reponses.length === 0
    ) {
      naviguer({ ecran: 'paquets' });
      return;
    }
    setQuitter(true);
  }

  // Clavier de séance (spécification §5) : touches lues par emplacement physique.
  /** Espace pendant l'alerte de fin de phase : continuer, ou terminer s'il n'y a pas de phase 2. */
  function finDePhase() {
    if (alerte && alerte.apprentissage > 0) {
      setAlerte(null);
      setVue('carte');
    } else {
      setAlerte(null);
      setVue('terminee');
      void rechargerJours();
    }
  }
  const etatClavier = useRef({
    vue,
    face,
    touches,
    quitter,
    retourner,
    noter,
    ouvrirModification,
    fermerModification,
    annuler,
    finDePhase,
  });
  etatClavier.current = {
    vue,
    face,
    touches,
    quitter,
    retourner,
    noter,
    ouvrirModification,
    fermerModification,
    annuler,
    finDePhase,
  };
  useEffect(() => {
    const appui = (e: KeyboardEvent) => {
      const etat = etatClavier.current;
      if (
        etat.vue === 'modification' &&
        e.key === 'Escape' &&
        !document.querySelector('.fc-dialogue__voile, .fc-menu, .fc-popover')
      ) {
        e.preventDefault();
        etat.fermerModification();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || etat.quitter || dansUnChamp(e.target)) return;
      if (document.querySelector('.fc-dialogue__voile, .fc-menu')) return;
      if (
        e.target instanceof HTMLButtonElement &&
        (e.code === TOUCHE_RETOURNER || e.key === 'Enter') &&
        etat.vue !== 'carte'
      )
        return;
      if (etat.vue === 'fin-de-phase' && e.code === TOUCHE_RETOURNER) {
        e.preventDefault();
        etat.finDePhase();
        return;
      }
      if ((etat.vue === 'terminee' || etat.vue === 'quittee') && e.key === 'Enter') {
        e.preventDefault();
        naviguer({ ecran: 'paquets' });
        return;
      }
      if (etat.vue !== 'carte') return;
      if (e.code === TOUCHE_RETOURNER) {
        if (
          e.target instanceof HTMLElement &&
          e.target.closest('.fc-notification__pile, .fc-bandeau')
        )
          return;
        e.preventDefault();
        if (!e.repeat) etat.retourner();
        return;
      }
      const action = actionDeLaTouche(etat.touches, e.code);
      if (action === null || e.repeat) return;
      e.preventDefault();
      if (action === 'modifier') {
        if (etat.face === 'verso') etat.ouvrirModification();
      } else if (action === 'annuler') {
        etat.annuler();
      } else {
        const note = NOTE_DE_L_ACTION[action];
        setNoteAppuyee(note);
        if (etat.face === 'recto') etat.retourner();
        else etat.noter(note);
      }
    };
    const relache = () => setNoteAppuyee(null);
    window.addEventListener('keydown', appui);
    window.addEventListener('keyup', relache);
    return () => {
      window.removeEventListener('keydown', appui);
      window.removeEventListener('keyup', relache);
    };
  }, []);

  // Bilan
  const revisees = new Set(reponses.map((r) => r.carteId));
  const repartition = { 1: 0, 2: 0, 3: 0, 4: 0 } as Record<Note, number>;
  for (const r of reponses) repartition[r.note] += 1;
  let acquises = 0;
  for (const id of revisees)
    if (s?.seance.cartes.get(id)?.fsrs.state === State.Review) acquises += 1;
  const prochaineEcheance = jours.get(paquetId)?.prochaineEcheance ?? null;
  const paquetVide = jours.get(paquetId)?.nombreCartes === 0;

  const phase = s?.seance.phase ?? 1;
  const totalPhase = totaux.get(phase) ?? s?.seance.file.length ?? 0;
  const progression =
    s && vue !== 'rien' && vue !== 'chargement' && vue !== 'erreur'
      ? { phase, faites: Math.max(0, totalPhase - s.seance.file.length), total: totalPhase }
      : null;

  let centre;
  switch (vue) {
    case 'chargement':
      centre = <Chargement type="rotation" texte="Préparation de la séance…" centre />;
      break;
    case 'erreur':
      centre = (
        <div className="fc-ecran-seance__vide">
          <EtatVide
            icone="cloud-off"
            titre="La séance n’a pas pu commencer"
            texte="Vérifiez votre connexion, puis réessayez."
            action={
              <Bouton variante="principal" icone="refresh-cw" onClick={() => void demarrer()}>
                Réessayer
              </Bouton>
            }
          />
        </div>
      );
      break;
    case 'rien':
      centre = paquetVide ? (
        <div className="fc-ecran-seance__vide">
          <EtatVide
            icone="layers"
            titre="Ce paquet est vide"
            texte={`${nomPaquet} ne contient encore aucune carte.`}
            action={
              <Bouton
                variante="principal"
                icone="plus"
                onClick={() => naviguer({ ecran: 'editeur', paquet: paquetId })}
              >
                Ajouter une carte
              </Bouton>
            }
          />
        </div>
      ) : (
        <div className="fc-ecran-seance__vide">
          <EtatVide
            icone="circle-check"
            titre="Rien à réviser"
            texte={`Aucune carte de ${nomPaquet} n’est due.${prochaineEcheance ? ` Prochaine carte due ${decrireEcheance(prochaineEcheance)}.` : ''}`}
            action={
              <Bouton variante="principal" onClick={() => naviguer({ ecran: 'paquets' })}>
                Revenir aux paquets
              </Bouton>
            }
          />
        </div>
      );
      break;
    case 'carte':
      centre =
        courante && carteMere ? (
          <>
            <CarteDeRevision
              key={courante.id}
              carte={carteMere}
              cle={courante.cle}
              face={face}
              raccourciRetourner="Espace"
              onRetourner={retourner}
            />
            {face === 'recto' && reglages?.retournementAutoSecondes && (
              <MinuteurDeRetournement
                key={`minuteur-${courante.id}`}
                duree={reglages.retournementAutoSecondes}
                affichage={reglages.affichageMinuteur}
                enPause={quitter}
                onEcoule={retourner}
              />
            )}
          </>
        ) : (
          <Chargement type="rotation" texte="Chargement de la carte…" centre />
        );
      break;
    case 'modification':
      centre = brouillon && carteMere && (
        <section aria-labelledby="modification-titre" className="fc-mode-modification">
          <header className="fc-mode-modification__entete">
            <h2 className="fc-mode-modification__titre" id="modification-titre">
              <Icone nom="pencil" />
              Modification de la carte
            </h2>
            <p className="fc-mode-modification__etat">
              <Icone nom="circle-pause" />
              Chronomètre en pause
            </p>
          </header>
          <FournisseurImages value={images.resoudre}>
            <FormulaireCarte
              ref={formulaire}
              brouillon={brouillon}
              onChange={(b) => {
                setBrouillon(b);
                if (Object.keys(erreursModification).length > 0)
                  setErreursModification(erreursDuBrouillon(b));
              }}
              erreurs={erreursModification}
              desactive={occupe}
              clesAvant={clesCartesFilles(carteMere)}
              typeFixe
              onEnregistrer={() => void enregistrerModification()}
              onAnnuler={fermerModification}
              envoyerImage={images.envoyer}
              resoudreImage={images.resoudre}
              couleursPersonnelles={preferences?.couleursSurlignage ?? []}
              onAjouterCouleur={(couleur) => {
                const actuelles = preferences?.couleursSurlignage ?? [];
                if (!actuelles.includes(couleur))
                  void depot.modifierPreferences({
                    couleursSurlignage: [...actuelles, couleur].slice(-50),
                  });
              }}
              onErreur={(message) => notifier({ message, statut: 'danger' })}
            />
          </FournisseurImages>
          <footer className="fc-mode-modification__pied">
            <div className="fc-mode-modification__actions">
              <Bouton aria-keyshortcuts="Escape" onClick={fermerModification} desactive={occupe}>
                Annuler <Touche>Échap</Touche>
              </Bouton>
              <Bouton
                variante="principal"
                aria-keyshortcuts="Meta+Enter"
                occupe={occupe}
                onClick={() => void enregistrerModification()}
              >
                Enregistrer <Touche>⌘ Entrée</Touche>
              </Bouton>
            </div>
          </footer>
        </section>
      );
      break;
    case 'fin-de-phase':
      centre = alerte && reglages && (
        <AlerteDeFinDePhase
          seuil={Math.round(seuilAlerteEffectif(reglages) * 100)}
          cartesRevisees={alerte.revisees}
          cartesEnApprentissage={alerte.apprentissage}
          onContinuer={() => {
            setAlerte(null);
            setVue('carte');
          }}
          onTerminer={() => {
            setAlerte(null);
            setVue('terminee');
            void rechargerJours();
          }}
        />
      );
      break;
    case 'terminee':
    case 'quittee':
      centre = (
        <BilanDeSeance
          issue={vue}
          paquet={nomPaquet}
          cartesRevisees={revisees.size}
          cartesDeLaSeance={tailleListe.current}
          duree={(Date.now() - debutSeance.current) / 60_000}
          phases={phase}
          repartition={repartition}
          acquises={acquises}
          enApprentissage={revisees.size - acquises}
          prochaineEcheance={prochaineEcheance}
          onRevenir={() => naviguer({ ecran: 'paquets' })}
          onStatistiques={() => naviguer({ ecran: 'statistiques', paquet: paquetId })}
          {...(vue === 'quittee' && s && !estTerminee(s.seance)
            ? { onReprendre: () => setVue('carte') }
            : {})}
        />
      );
      break;
  }

  const notationVisible = vue === 'carte' || vue === 'modification';

  return (
    <FournisseurImages value={images.resoudre}>
      <div className="fc-fenetre fc-fenetre--seance">
        <BarreDeSeance
          face={face}
          actionsCarte={vue === 'carte'}
          filDAriane={
            reglages?.afficherFilAriane && courante && vue === 'carte' ? courante.chemin : null
          }
          progression={progression}
          annulationPossible={reponses.length > 0 && vue !== 'modification'}
          raccourciModifier={touches.modifier ? caractere(touches.modifier) : 'R'}
          raccourciAnnuler={touches.annuler ? caractere(touches.annuler) : null}
          desactivee={occupe}
          onQuitter={demanderQuitter}
          onModifier={ouvrirModification}
          onSuspendre={suspendre}
          onAnnuler={annuler}
        />
        <main className="fc-fenetre__principale">
          {horsLigne && (
            <div className="fc-ecran-seance__bandeau">
              <Bandeau
                statut="danger"
                icone="cloud-off"
                annonce
                titre="Connexion perdue."
                message="La dernière réponse n’a pas été enregistrée ; la séance reprendra dès le retour de la connexion."
                action={{ libelle: 'Réessayer', icone: 'refresh-cw', onAction: () => horsLigne() }}
              />
            </div>
          )}
          {centre}
        </main>
        {notationVisible && (
          <BarreDeNotation
            face={face}
            durees={durees}
            touches={{
              1: caractere(touches.a_revoir ?? 'KeyL'),
              2: caractere(touches.difficile ?? 'Semicolon'),
              3: caractere(touches.bien ?? 'Quote'),
              4: caractere(touches.facile ?? 'Backslash'),
            }}
            raccourciRetourner="Espace"
            noteAppuyee={noteAppuyee}
            desactivee={vue === 'modification' || occupe || horsLigne !== null || !carteMere}
            onRetourner={retourner}
            onNoter={noter}
          />
        )}
      </div>
      {quitter && (
        <Dialogue
          titre="Quitter la séance ?"
          texte="Les réponses déjà données sont enregistrées. Les cartes restantes vous attendront à la prochaine séance."
          actionSecondaire="Continuer la séance"
          actionPrincipale={{
            libelle: 'Quitter',
            onAction: () => {
              setQuitter(false);
              setVue('quittee');
              void rechargerJours();
            },
          }}
          onFermer={() => setQuitter(false)}
        />
      )}
    </FournisseurImages>
  );
}
